import React, { useEffect, useState } from 'react';
import { Card, Select, Button, Space, Typography, App } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getUnifiedCompanyUsers,
  setDocumentTaskRouting,
} from '@/services/unifiedTask.service';

const { Text } = Typography;

export interface TaskRoutingCardProps {
  sourceDocumentType: string;
  sourceDocumentId: string;
  nextActionUserId?: string | null;
  documentOwnerLabel: string;
  documentOwnerUserId?: string | null;
  documentOwnerEditHint?: string;
  disabled?: boolean;
  /** Extra query keys to invalidate after save (e.g. ['purchase-order', id]) */
  invalidateQueryKeys?: unknown[][];
}

export const TaskRoutingCard: React.FC<TaskRoutingCardProps> = ({
  sourceDocumentType,
  sourceDocumentId,
  nextActionUserId,
  documentOwnerLabel,
  documentOwnerUserId,
  documentOwnerEditHint,
  disabled = false,
  invalidateQueryKeys = [],
}) => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<string | undefined>(undefined);

  const { data: companyUsers = [], isLoading: usersLoading } = useQuery({
    queryKey: ['unified-company-users'],
    queryFn: getUnifiedCompanyUsers,
    staleTime: 120_000,
  });

  useEffect(() => {
    setDraft(nextActionUserId || undefined);
  }, [sourceDocumentId, nextActionUserId]);

  const userLabel = (uid?: string | null) => {
    if (!uid) return '—';
    return companyUsers.find((u) => u.id === uid)?.name || 'Unknown user';
  };

  const saveMutation = useMutation({
    mutationFn: (uid: string | undefined) =>
      setDocumentTaskRouting({
        source_document_type: sourceDocumentType,
        source_document_id: sourceDocumentId,
        next_action_user_id: uid ?? null,
      }),
    onSuccess: (data) => {
      message.success(
        data.tasks_updated > 0
          ? `Next action saved (${data.tasks_updated} task${data.tasks_updated === 1 ? '' : 's'} updated)`
          : 'Next action assignee saved'
      );
      queryClient.invalidateQueries({ queryKey: ['unified-tasks'] });
      queryClient.invalidateQueries({ queryKey: ['unified-company-users'] });
      for (const key of invalidateQueryKeys) {
        queryClient.invalidateQueries({ queryKey: key });
      }
    },
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      message.error(err?.response?.data?.message || err?.message || 'Save failed');
    },
  });

  const unchanged = (draft || undefined) === (nextActionUserId || undefined);

  return (
    <Card size="small" title="Task routing" style={{ marginBottom: 16 }}>
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <div>
          <Text type="secondary">{documentOwnerLabel}</Text>
          <div>
            <Text>{userLabel(documentOwnerUserId)}</Text>
          </div>
          {documentOwnerEditHint ? (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {documentOwnerEditHint}
            </Text>
          ) : null}
        </div>
        <div>
          <Text type="secondary">Next action (unified tasks)</Text>
          <div style={{ marginTop: 8 }}>
            <Space wrap>
              <Select
                showSearch
                allowClear
                disabled={disabled}
                placeholder="Who should act next?"
                optionFilterProp="label"
                style={{ minWidth: 280 }}
                loading={usersLoading}
                value={draft}
                onChange={(v) => setDraft(v)}
                options={companyUsers.map((u) => ({ value: u.id, label: u.name }))}
              />
              <Button
                type="primary"
                disabled={disabled || unchanged}
                loading={saveMutation.isPending}
                onClick={() => saveMutation.mutate(draft)}
              >
                Save
              </Button>
            </Space>
          </div>
        </div>
      </Space>
    </Card>
  );
};
